import { useEffect, useMemo, useRef, useState } from "react";
import { usePdfZoomPan } from "../lib/usePdfZoomPan";

/**
 * T21: Zulagenblatt-Pendant zu PdfWithHighlights (Stundenliste) und
 * ExpensePdfHighlights (Diäten). Rendert die Zulagenblatt-Vorschau mit absolut
 * positionierten Overlay-Divs — EINE Zelle je Zulagenart-Spalte × der
 * aufgedruckten Monats-Summenzeile — die bei Hover die zugehörige
 * Summen-Tabellenzelle und umgekehrt hervorheben (bidirektionaler Link über
 * `activeKey` + data-key-Attribute). DIESELBE Mechanik + DIESELBEN CSS-Klassen
 * wie die beiden Bestands-Overlays, nur auf die eine Summenzeile angewandt.
 *
 * Geometrie kommt aus den Werten, die die OCR zum LESEN genutzt hat:
 *   - Spalten-x-Spanne je Zulagenart: data.meta.layout.spalten_geom[<key>]
 *   - y-Band der Summenzeile:         data.blatt_summe.band_pct
 * Beides ist im (aufgerichteten) Preview-Frame → column-precise, kein geratenes
 * Aufteilen (die x-Spanne ist exakt die vom Reader gelesene Spalte).
 */
export interface ZulagenSummeCell {
  /** Spalten-Key (z. B. "untertage") — verbindet Overlay ↔ Summen-Tabellenzeile. */
  key: string;
  /** x-Spanne in % (0–100) der Bildbreite. */
  leftPct: number;
  rightPct: number;
}

interface Props {
  imageUrl: string;
  pdfHrefForOpen: string;
  /** Overlay-Zellen (Zulagenart-Spalten mit Geometrie). */
  cells: ReadonlyArray<ZulagenSummeCell>;
  /** y-Band der Summenzeile in % (0–100) der Bildhöhe. */
  bandTopPct: number;
  bandBottomPct: number;
  activeKey: string | null;
  onHover: (key: string | null) => void;
  /** Klick auf eine Overlay-Zelle → zum Gegenstück springen (die zugehörige
   *  Summen-Tabellenzeile rechts scrollen + pulsen). Teil der vererbten
   *  Klick-zum-Springen-Mechanik (useCellHighlight). */
  onCellClick?: (key: string) => void;
  interactive?: boolean;
  onReady?: () => void;
}

export function ZulagenblattHighlights({
  imageUrl,
  pdfHrefForOpen,
  cells,
  bandTopPct,
  bandBottomPct,
  activeKey,
  onHover,
  onCellClick,
  interactive = true,
  onReady,
}: Props) {
  // Bild wird mit object-fit:contain gerendert → Letterboxing wenn Container-
  // Aspect ≠ Bild-Aspect. Overlays nach % des CONTAINERs würden bei Resize
  // driften. Wir tracken die tatsächliche Bild-Bounding-Box (ResizeObserver +
  // naturalWidth/Height) und legen die Overlay-Ebene exakt aufs Bild-Rechteck.
  // (Verbatim aus ExpensePdfHighlights — dort war Resize-Drift ein realer Bug.)
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [imgBox, setImgBox] = useState<{ left: number; top: number; width: number; height: number } | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => { setLoaded(false); setImgBox(null); }, [imageUrl]);

  useEffect(() => {
    const container = containerRef.current;
    const img = imgRef.current;
    if (!container || !img) return;
    const compute = () => {
      const nw = img.naturalWidth, nh = img.naturalHeight;
      if (!nw || !nh) return;
      const cw = container.clientWidth, ch = container.clientHeight;
      if (!cw || !ch) return;
      const naturalRatio = nw / nh;
      const containerRatio = cw / ch;
      let rw, rh, ox, oy;
      if (containerRatio > naturalRatio) {
        rh = ch; rw = ch * naturalRatio; ox = (cw - rw) / 2; oy = 0;
      } else {
        rw = cw; rh = cw / naturalRatio; ox = 0; oy = (ch - rh) / 2;
      }
      setImgBox({ left: ox, top: oy, width: rw, height: rh });
    };
    const markReady = () => { compute(); setLoaded(true); onReady?.(); };
    compute();
    if (img.complete && img.naturalWidth) markReady();
    const ro = new ResizeObserver(compute);
    ro.observe(container);
    img.addEventListener("load", markReady);
    img.addEventListener("error", markReady);
    return () => {
      ro.disconnect();
      img.removeEventListener("load", markReady);
      img.removeEventListener("error", markReady);
    };
  }, [imageUrl, onReady]);

  const overlays = useMemo(() => {
    const heightPct = bandBottomPct - bandTopPct;
    return cells.map((c) => ({
      key: c.key,
      style: {
        top: `${bandTopPct}%`,
        left: `${c.leftPct}%`,
        width: `${c.rightPct - c.leftPct}%`,
        height: `${heightPct}%`,
      } as React.CSSProperties,
      isActive: activeKey === c.key,
    }));
  }, [cells, bandTopPct, bandBottomPct, activeKey]);

  const overlayLayerStyle: React.CSSProperties = imgBox
    ? { position: "absolute", left: imgBox.left, top: imgBox.top, width: imgBox.width, height: imgBox.height }
    : { position: "absolute", inset: 0 };

  // Zoom+Pan (geteilter Hook, Muster PdfWithHighlights — Thomas 24.07.)
  const zp = usePdfZoomPan();

  return (
    <div ref={containerRef}
         className={`pdf-with-highlights${interactive ? "" : " readonly"}${zp.zoom > 1.01 ? " zoomed" : ""}`}
         onWheel={zp.onWheel}>
      <div className="pdf-zoom-wrapper"
           style={{ transform: zp.transform, transformOrigin: "0 0" }}
           onPointerDown={zp.onPointerDown} onPointerMove={zp.onPointerMove}
           onPointerUp={zp.onPointerUp} onPointerCancel={zp.onPointerUp}>
      <a href={pdfHrefForOpen} target="_blank" rel="noopener noreferrer" title="Original-PDF öffnen" className="pdf-link"
         onClick={(e) => { if (zp.zoom > 1.01) e.preventDefault(); }}>
        <img ref={imgRef} src={imageUrl} alt="Zulagenblatt-Scan" className="pdf-image" loading="eager" decoding="async" draggable={false} />
      </a>
      {interactive && loaded && imgBox && (
        <div className="pdf-overlay-layer" style={overlayLayerStyle} aria-hidden="true">
          {overlays.map(({ key, style, isActive }) => (
            <div
              key={key}
              className={`pdf-overlay-cell src-detected${isActive ? " active" : ""}`}
              data-key={key}
              data-hl-side="source"
              data-hl-match={key}
              style={style}
              onPointerEnter={() => onHover(key)}
              onPointerLeave={() => onHover(null)}
              onClick={onCellClick ? () => onCellClick(key) : undefined}
            />
          ))}
        </div>
      )}
      </div>
      <div className="pdf-zoom-controls" aria-label="Zoom-Steuerung">
        <button type="button" onClick={() => zp.setZoom((z) => zp.clampZoom(z / 1.25))} disabled={zp.zoom <= 1.01} title="Zoom out">−</button>
        <span className="pdf-zoom-level" aria-live="polite">{Math.round(zp.zoom * 100)}%</span>
        <button type="button" onClick={() => zp.setZoom((z) => zp.clampZoom(z * 1.25))} disabled={zp.zoom >= 3.99} title="Zoom in">+</button>
        <button type="button" onClick={zp.resetZoom} disabled={zp.zoom <= 1.01 && zp.pan.x === 0 && zp.pan.y === 0} title="Reset">⟲</button>
      </div>
    </div>
  );
}
